import { getCanonicalUrl } from "./canonical";

const MEDIA_PREFIX = "/media";

const LEGACY_STATIC_HOSTS = new Set([
  "smartbathroom.ir",
  "static.smartbathroom.ir",
]);

function extractPathFromInput(path) {
  if (!path) return null;

  const finalPath =
    typeof path === "object"
      ? path.url ||
        path.image ||
        path.path ||
        path.originalPath ||
        path.preview ||
        path.image_url ||
        path.src ||
        path.thumbnail
      : path;

  if (!finalPath || typeof finalPath !== "string") return null;

  const trimmed = finalPath.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("blob:")) return trimmed;

  if (trimmed.startsWith("/image/") || trimmed.startsWith("image/")) {
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);

      if (url.pathname.startsWith(`${MEDIA_PREFIX}/`)) {
        return url.pathname;
      }

      if (LEGACY_STATIC_HOSTS.has(url.hostname)) {
        return url.pathname;
      }

      return trimmed;
    } catch {
      return null;
    }
  }

  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

/**
 * Same-domain image URL (no static subdomain) served via /media rewrite.
 * Relative paths keep requests on the main origin for better performance.
 */
export const getImageUrl = (path) => {
  const storagePath = extractPathFromInput(path);
  if (!storagePath) return null;

  if (storagePath.startsWith("/image/") || storagePath.startsWith("blob:")) {
    return storagePath;
  }

  if (/^https?:\/\//i.test(storagePath)) {
    return storagePath;
  }

  if (storagePath.startsWith(`${MEDIA_PREFIX}/`)) {
    return storagePath;
  }

  return `${MEDIA_PREFIX}${storagePath}`;
};

/**
 * Absolute same-domain URL for Open Graph, Twitter cards, and JSON-LD.
 */
export const getAbsoluteImageUrl = (path) => {
  const url = getImageUrl(path);
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return getCanonicalUrl(url);
};

/**
 * Get optimized Next.js Image props for best performance
 * @param {string} src - Image source URL
 * @param {object} options - Additional options
 * @returns {object} - Props object for Next.js Image component
 */
export const getOptimizedImageProps = (src, options = {}) => {
  const {
    priority = false,
    quality = 85,
    sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
    ...rest
  } = options;

  return {
    src: getImageUrl(src),
    quality,
    sizes,
    priority,
    fetchPriority: priority ? "high" : "auto",
    loading: priority ? "eager" : "lazy",
    ...rest,
  };
};
